import type { IChoiceGroupOption } from "@fluentui/react";
import { ChoiceGroup } from "@fluentui/react";
import React, { useEffect, useState, type FormEvent } from "react";
import type { NodeProps, Node } from "@xyflow/react";
import type { ExtraNodeData, InterfaceToRecord } from "./action-config-node";
import ActionConfigNode, { antennaOptions, zoneOptions } from "./action-config-node";
import type { StationsInZoneData } from "../../../api";
import { Antenna, Zone } from "../../../api";
import { useTranslation } from "react-i18next";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type StationsInZoneNode = Node<InterfaceToRecord<StationsInZoneData & ExtraNodeData>>;

export default function StationsInZone(props: NodeProps<StationsInZoneNode>): JSX.Element {
    const { t } = useTranslation();
    const [selectedAntenna, setSelectedAntenna] = useState(props.data.antenna ?? Antenna.A);
    const [selectedZone, setSelectedZone] = useState(props.data.zone ?? Zone.ONE);
    const updateNode = useUpdateNodeData<StationsInZoneNode>(props.id);

    // We have to set the data to prevent undefined values
    useEffect(() => {
        updateNode({ zone: selectedZone, antenna: selectedAntenna });
    }, [selectedZone, selectedAntenna, updateNode]);

    function onZoneChange(
        _e?: FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption,
    ): void {
        if (!option) {
            return;
        }
        setSelectedZone(option.key as Zone);
    }

    function onAntennaChange(
        _e?: FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption,
    ): void {
        if (!option) {
            return;
        }
        setSelectedAntenna(option.key as Antenna);
    }

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <ChoiceGroup
                options={zoneOptions(t)}
                selectedKey={selectedZone}
                onChange={onZoneChange}
            />
            <ChoiceGroup
                options={antennaOptions(t)}
                selectedKey={selectedAntenna}
                onChange={onAntennaChange}
            />
        </ActionConfigNode>
    );
}
